import type { ComponentType } from 'react'
import { Box, Typography, Divider, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { SelectableCard } from '@dhl/components'

import { formattedOptionPrice } from '~/basket/view/util/price'
import { zero } from '~/basket/model/utils/price'

import { OptionValue } from './model'

export type OptionProps<A = {}> = {
  option: OptionValue<A>
}

type Props<A> = OptionProps<A> & {
  dataTestPrefix?: string
  onClick: () => void
  isSelected: boolean
  title: ComponentType<{ option: A }>
  description: ComponentType<{ option: A }>
  icon: ComponentType<{ optionKey: string; carrier: string }>
}

export function OptionCard<A>({
  dataTestPrefix = '',
  option,
  onClick,
  isSelected,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  icon: Icon,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  description: Description,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  title: Title,
}: Props<A>) {
  const { t } = useTranslation()
  const isExtraSmall = useMediaQuery('(max-width:320px)')

  const id = `pwa-shipmentoption-${option.key}`
  const dataTest = [dataTestPrefix?.toLowerCase(), (option.carrier ?? '').toLowerCase(), id].filter(Boolean).join('-')

  return (
    <SelectableCard isSelected={isSelected} onClick={onClick} data-test={dataTest} id={id}>
      <Box display="flex" alignItems="center" height="100%" position="relative">
        <Box m={isExtraSmall ? 0 : 1} display="flex" alignItems="center">
          <Icon optionKey={option.key} carrier={option.carrier ? option.carrier : ''} />
        </Box>
        <Divider
          orientation="vertical"
          sx={(theme) => ({
            borderTop: `${theme.spacing(1)} solid white`,
            borderBottom: `${theme.spacing(1)} solid white`,
          })}
        />
        <Box
          ml={isExtraSmall ? 1 : 2}
          my={2}
          display="flex"
          flexDirection="column"
          justifyContent="center"
          flexGrow={1}
        >
          <Title option={option} />

          <Box mr={isExtraSmall ? 10 : 11} mt={0.25}>
            <Description option={option} />
          </Box>
        </Box>
        <Box display="flex" mx={isExtraSmall ? 1.5 : 2} position="absolute" right="0">
          <Typography noWrap>{formattedOptionPrice(option.price || zero, t('free'))}</Typography>
        </Box>
      </Box>
    </SelectableCard>
  )
}
